import { useEffect } from 'react';

import { useAppSelector } from '@/hooks/redux';
import { selectAccessToken, selectIsAuthenticated } from '@/store/authSlice';
import { refreshAuthSession } from '@/lib/auth-refresh';
import { registerSocketTokenProvider, syncSocketAuth, disconnectSocket } from '@/lib/socket';
import { store } from '@/store';

/** Refresh access token before the 15m JWT expiry so socket reconnects stay authenticated. */
const PROACTIVE_REFRESH_MS = 12 * 60 * 1000;

/**
 * Keeps Socket.IO auth in sync with the Redux access token and proactively refreshes
 * the session so long-lived tabs do not lose realtime updates after JWT expiry.
 */
export default function SocketAuthSync() {
  const accessToken = useAppSelector(selectAccessToken);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  useEffect(() => {
    registerSocketTokenProvider(() => store.getState().auth.accessToken);
  }, []);

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      syncSocketAuth(accessToken);
    } else {
      disconnectSocket();
    }
  }, [accessToken, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      void refreshAuthSession();
    }, PROACTIVE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return null;
}
